// ============================================================
//  Page /carte — pilote l'editeur MapEditor + liste des balises
// ============================================================

import { useEffect, useMemo, useState } from "react";
import * as api from "../lib/api";
import type { Balise, Lieu } from "../lib/types";
import { MapEditor } from "../components/MapEditor";
import { SatelliteMapEditor } from "../components/SatelliteMapEditor";

/** Catalogue de personnages 3D dispo dans le player (pack Quaternius CC0).
 *  Les .gltf correspondants sont dans apps/player/public/models/<key>.gltf. */
const MODEL_PRESETS = [
  { key: "alpaca",      label: "🦙 Alpaga" },
  { key: "bull",        label: "🐂 Taureau" },
  { key: "cow",         label: "🐄 Vache" },
  { key: "deer",        label: "🦌 Biche" },
  { key: "stag",        label: "🦌 Cerf (bois)" },
  { key: "donkey",      label: "🫏 Âne" },
  { key: "fox",         label: "🦊 Renard" },
  { key: "horse",       label: "🐎 Cheval" },
  { key: "horse_white", label: "🐎 Cheval blanc" },
  { key: "husky",       label: "🐺 Husky" },
  { key: "shibainu",    label: "🐕 Shiba Inu" },
  { key: "wolf",        label: "🐺 Loup" },
];

// V1 : id fixe seede dans la DB
const STALINGRAD_ID = "11111111-1111-1111-1111-111111111111";

type ViewMode = "image" | "satellite";

export function CarteEditor() {
  const [lieu, setLieu] = useState<Lieu | null>(null);
  const [balises, setBalises] = useState<Balise[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    return (localStorage.getItem("admin-view-mode") as ViewMode) || "image";
  });

  useEffect(() => {
    localStorage.setItem("admin-view-mode", viewMode);
  }, [viewMode]);

  // Image url : on sert depuis le public/ du SPA (asset statique).
  const imageUrl = useMemo(() => {
    return `/${lieu?.image_key ?? "stalingrad-map.png"}`;
  }, [lieu?.image_key]);

  useEffect(() => {
    (async () => {
      try {
        const [l, bs] = await Promise.all([
          api.getLieu(STALINGRAD_ID),
          api.listBalises(STALINGRAD_ID),
        ]);
        setLieu(l);
        setBalises(bs);
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleCreate(pos: { x: number; y: number }) {
    try {
      const next = await api.createBalise(STALINGRAD_ID, {
        position_x: pos.x,
        position_y: pos.y,
      });
      setBalises((prev) => [...prev, next]);
      setSelectedId(next.id);
    } catch (e) {
      alert("Création échouée : " + (e as Error).message);
    }
  }

  async function handleCreateSatellite(coords: { lat: number; lng: number }) {
    try {
      const next = await api.createBalise(STALINGRAD_ID, {
        position_x: 0.5, // requis par le schema, valeur centre par défaut
        position_y: 0.5,
        lat: coords.lat,
        lng: coords.lng,
      });
      setBalises((prev) => [...prev, next]);
      setSelectedId(next.id);
    } catch (e) {
      alert("Création échouée : " + (e as Error).message);
    }
  }

  async function handleMove(id: string, pos: { x: number; y: number }) {
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, position_x: pos.x, position_y: pos.y } : b))
    );
    try {
      await api.updateBalise(id, { position_x: pos.x, position_y: pos.y });
    } catch (e) {
      alert("Déplacement échoué : " + (e as Error).message);
    }
  }

  async function handleMoveSatellite(id: string, coords: { lat: number; lng: number }) {
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, lat: coords.lat, lng: coords.lng } : b))
    );
    try {
      await api.updateBalise(id, { lat: coords.lat, lng: coords.lng });
    } catch (e) {
      alert("Déplacement échoué : " + (e as Error).message);
    }
  }

  async function handleRename(id: string, nom: string) {
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, nom: nom || null } : b))
    );
    try {
      await api.updateBalise(id, { nom: nom || null });
    } catch (e) {
      alert("Renommage échoué : " + (e as Error).message);
    }
  }

  async function handleArVisuel(
    id: string,
    field: "emoji_3d" | "couleur_3d" | "model_3d_key",
    value: string
  ) {
    const v = value.trim() === "" ? null : value;
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, [field]: v } : b))
    );
    try {
      await api.updateBalise(id, { [field]: v });
    } catch (e) {
      alert("Maj visuel AR échouée : " + (e as Error).message);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Supprimer cette balise ?")) return;
    const backup = balises;
    setBalises((prev) => prev.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
    try {
      await api.deleteBalise(id);
    } catch (e) {
      alert("Suppression échouée : " + (e as Error).message);
      setBalises(backup);
    }
  }

  // ---------- GPS handlers ----------
  function parseCoord(v: string): number | null {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  async function handleGps(id: string, value: string, field: "lat" | "lng") {
    const num = parseCoord(value);
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, [field]: num } : b))
    );
    const current = balises.find((b) => b.id === id);
    if (!current) return;
    const newLat = field === "lat" ? num : current.lat;
    const newLng = field === "lng" ? num : current.lng;
    // On sauvegarde uniquement si les deux sont set (ou les deux null)
    if ((newLat == null) === (newLng == null)) {
      try {
        await api.updateBalise(id, { lat: newLat, lng: newLng });
      } catch (e) {
        alert("Erreur GPS : " + (e as Error).message);
      }
    }
  }

  async function clearGps(id: string) {
    setBalises((prev) =>
      prev.map((b) => (b.id === id ? { ...b, lat: null, lng: null } : b))
    );
    try {
      await api.updateBalise(id, { lat: null, lng: null });
    } catch (e) {
      alert("Erreur : " + (e as Error).message);
    }
  }

  async function useMyPosition(id: string) {
    if (!navigator.geolocation) {
      alert("GPS non supporté par ton navigateur");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setBalises((prev) =>
          prev.map((b) => (b.id === id ? { ...b, lat, lng } : b))
        );
        try {
          await api.updateBalise(id, { lat, lng });
        } catch (e) {
          alert("Erreur sauvegarde GPS : " + (e as Error).message);
        }
      },
      (err) => alert("Position refusée : " + err.message),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  if (loading) return <div className="page-loading">Chargement…</div>;
  if (error) return <div className="page-error">Erreur : {error}</div>;
  if (!lieu) return <div className="page-error">Lieu introuvable</div>;

  const selected = balises.find((b) => b.id === selectedId);

  return (
    <div className="carte-page">
      <header className="carte-head">
        <div>
          <h1>{lieu.nom}</h1>
          <p className="carte-sub">
            {balises.length} balise{balises.length > 1 ? "s" : ""}
            {viewMode === "satellite" && (
              <> · {balises.filter((b) => b.lat != null).length} avec GPS</>
            )}
            {" · "}
            <span className="muted">clique sur la carte pour en ajouter</span>
          </p>
        </div>
        <div className="view-toggle">
          <button
            className={viewMode === "image" ? "tog-btn active" : "tog-btn"}
            onClick={() => setViewMode("image")}
            title="Carte stylisée du parc"
          >
            📷 Carte
          </button>
          <button
            className={viewMode === "satellite" ? "tog-btn active" : "tog-btn"}
            onClick={() => setViewMode("satellite")}
            title="Vue satellite Esri pour placer les coords GPS"
          >
            🛰️ Satellite
          </button>
        </div>
      </header>

      <div className="carte-body">
        {viewMode === "image" ? (
          <MapEditor
            lieu={lieu}
            imageUrl={imageUrl}
            balises={balises}
            selectedId={selectedId}
            onCreate={handleCreate}
            onMove={handleMove}
            onSelect={setSelectedId}
          />
        ) : (
          <SatelliteMapEditor
            balises={balises}
            selectedId={selectedId}
            onCreate={handleCreateSatellite}
            onMove={handleMoveSatellite}
            onSelect={setSelectedId}
          />
        )}

        <aside className="balises-panel">
          <h3>Balises ({balises.length})</h3>
          {balises.length === 0 && (
            <p className="hint">Clique sur la carte pour poser ta première balise.</p>
          )}
          <ul className="balises-list">
            {balises.map((b, i) => (
              <li
                key={b.id}
                className={b.id === selectedId ? "balise-row selected" : "balise-row"}
                onClick={() => setSelectedId(b.id)}
              >
                <span className="balise-num">{i + 1}</span>
                <span className="balise-nom">{b.nom ?? "Sans nom"}</span>
                <span className="balise-pos">
                  ({Math.round(b.position_x * 100)}%, {Math.round(b.position_y * 100)}%)
                </span>
              </li>
            ))}
          </ul>

          {selected && (
            <div className="balise-edit">
              <h4>Édition</h4>
              <label>
                Nom interne
                <input
                  type="text"
                  value={selected.nom ?? ""}
                  onChange={(e) => handleRename(selected.id, e.target.value)}
                  placeholder="ex : près du grand chêne"
                  maxLength={200}
                />
              </label>

              <div className="gps-edit">
                <h5>Coords GPS (mode AR)</h5>
                <div className="gps-inputs">
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="Latitude"
                    value={selected.lat ?? ""}
                    onChange={(e) => handleGps(selected.id, e.target.value, "lat")}
                  />
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="Longitude"
                    value={selected.lng ?? ""}
                    onChange={(e) => handleGps(selected.id, e.target.value, "lng")}
                  />
                </div>
                <button onClick={() => useMyPosition(selected.id)} className="btn-secondary btn-mini">
                  📍 Utiliser ma position
                </button>
                <button onClick={() => clearGps(selected.id)} className="btn-link">
                  Effacer les coords
                </button>
                {selected.lat != null && selected.lng != null && (
                  <a
                    href={`https://www.google.com/maps?q=${selected.lat},${selected.lng}`}
                    target="_blank"
                    rel="noopener"
                    className="btn-link"
                  >
                    🗺️ Voir sur Google Maps
                  </a>
                )}
              </div>

              <div className="ar-visuel-edit">
                <h5>Objet AR</h5>
                <p className="hint-mini">
                  Priorité : modèle 3D &gt; emoji &gt; cube orange par défaut.
                </p>
                <label>
                  Modèle 3D (animal)
                  <select
                    value={selected.model_3d_key ?? ""}
                    onChange={(e) => handleArVisuel(selected.id, "model_3d_key", e.target.value)}
                  >
                    <option value="">— Aucun (utilise emoji ou cube) —</option>
                    {MODEL_PRESETS.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Emoji (fallback si pas de modèle)
                  <input
                    type="text"
                    value={selected.emoji_3d ?? ""}
                    onChange={(e) => handleArVisuel(selected.id, "emoji_3d", e.target.value)}
                    placeholder="🌳"
                    maxLength={12}
                  />
                </label>
                <label>
                  Couleur du badge
                  <input
                    type="color"
                    value={selected.couleur_3d ?? "#FF9800"}
                    onChange={(e) => handleArVisuel(selected.id, "couleur_3d", e.target.value)}
                  />
                </label>
                {(selected.emoji_3d || selected.couleur_3d || selected.model_3d_key) && (
                  <button
                    className="btn-link"
                    onClick={() => {
                      handleArVisuel(selected.id, "emoji_3d", "");
                      handleArVisuel(selected.id, "couleur_3d", "");
                      handleArVisuel(selected.id, "model_3d_key", "");
                    }}
                  >
                    Réinitialiser
                  </button>
                )}
              </div>

              <p className="balise-id">ID : <code>{selected.id}</code></p>
              <button onClick={() => handleDelete(selected.id)} className="btn-danger">
                🗑 Supprimer la balise
              </button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
